import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

/**
 * Tabs list (the bar that holds the Tab triggers + the sliding Indicator).
 *
 * `isolate` creates a stacking context so the Indicator can sit behind the
 * tab labels. In horizontal mode `clip-path: inset(0)` clips the active
 * Indicator's connector flares at the list edges — that is what makes the
 * first tab show only its right flare and the last tab only its left flare,
 * with no per-tab suppression needed.
 */
export const tabsListVariants = tv({
  base: [
    'relative isolate inline-flex',
    // self-start prevents a flex parent (Root is `flex flex-col`) from
    // stretching the list to its own width — without this, the list extends
    // past the last tab and clip-path's right edge lands beyond the rightmost
    // flare instead of on it, leaking the right flare on the last tab.
    'self-start',
    'data-[orientation=horizontal]:flex-row data-[orientation=horizontal]:items-stretch',
    'data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
    // 4px between tabs — horizontal (Figma: pills are 90px on a 94px pitch) and
    // vertical mirror the same spacing token. The pill and the hover overlay
    // both fill their tab box edge-to-edge (L/R for horizontal, top/bottom for
    // vertical), so this gap IS the inter-tab gap — 4px. The overlay must NOT
    // add its own inset on that axis, or the side gaps become 4+2=6px and stop
    // matching the 4px bottom gap.
    'data-[orientation=horizontal]:gap-1 data-[orientation=vertical]:gap-1',
    // Clip ONLY left/right (so the first tab shows just its right flare and the
    // last tab just its left). Top/bottom are left open (-9999px) — clipping the
    // bottom at the fractional row boundary added an anti-aliased cut that let
    // the page bg leak through as a 1px seam between the pill and the panel.
    'data-[orientation=horizontal]:[clip-path:inset(-9999px_0px_-9999px_0px)]',
  ],
  variants: {
    // `inline-flex` from the base is sufficient — only the width changes.
    fullWidth: {
      true: 'w-full',
    },
    // Detached pills have no flares to clip at the list edges.
    detached: {
      true: 'data-[orientation=horizontal]:[clip-path:none]',
    },
  },
})

/**
 * Sliding active indicator — a single absolutely-positioned element that
 * carries the selected-tab pill (paper-elevation2) and, in horizontal mode,
 * the connector flares. Base UI writes the `--active-tab-*` CSS vars onto it;
 * transitioning left/top/width/height makes the pill glide between tabs
 * (the "Chrome-tab" feel). Sits behind the tab labels via `z-[-1]`.
 */
export const tabsIndicatorVariants = tv({
  base: [
    // Arbitrary z value — the stock z-index scale emits no CSS in this preset.
    'pointer-events-none absolute z-[-1]',
    'left-[var(--active-tab-left)] top-[var(--active-tab-top)] h-[var(--active-tab-height)] w-[var(--active-tab-width)]',
    'transition-[left,top,width,height] duration-200 ease-out',
  ],
  variants: {
    orientation: {
      horizontal: [
        'rounded-t-[8px]',
        // Connector flares — CONCAVE lune (arc centred on the outer-top corner)
        // matching the Figma asset shape, sized 8px to equal the pill's top
        // corner radius. Flush at the pill edge (-8px). Mask carves the top
        // corner (transparent inside the 8px circle, opaque outside) so the
        // side sweeps down into the panel.
        'before:absolute before:bottom-0 before:-left-[8px] before:size-[8px] before:content-[""]',
        'before:[mask-image:radial-gradient(circle_at_top_left,transparent_7.5px,black_8px)]',
        'after:absolute after:bottom-0 after:-right-[8px] after:size-[8px] after:content-[""]',
        'after:[mask-image:radial-gradient(circle_at_top_right,transparent_7.5px,black_8px)]',
      ],
      // Fully-rounded pill; no flares in vertical.
      vertical: 'rounded-[8px]',
    },
    // Paper-elevation token used for the pill background. Mirrors the DS scale
    // `paper-elevation0`..`paper-elevation5`. Default is 3 (the pre-existing
    // pill colour, #1b1b1d). Listed as full class literals so the Tailwind JIT
    // picks them up. The flare bg classes are emitted only for the horizontal
    // orientation via `compoundVariants` below (vertical has no flare pseudos).
    paperLevel: {
      0: 'bg-paper-elevation0',
      1: 'bg-paper-elevation1',
      2: 'bg-paper-elevation2',
      3: 'bg-paper-elevation3',
      4: 'bg-paper-elevation4',
      5: 'bg-paper-elevation5',
    },
    // Standalone pill — fully rounded like the vertical pill, connector
    // flares hidden. For tab strips with no attached Panel to merge into.
    // Classes are emitted via compoundVariants (horizontal only) below.
    detached: {
      true: '',
    },
    // Active-indicator shape. `pill` (default) is the chrome-tab pill above;
    // `underline` swaps it for a 2px accent bar hugging the list's bottom
    // edge — no pill surface, no flares. Horizontal only (like the flares);
    // classes are emitted via compoundVariants below, so vertical keeps the
    // pill.
    indicator: {
      pill: '',
      underline: '',
    },
    // Mirrors Root's `color` so the underline carries the same accent as the
    // selected label. Consumed by the underline compoundVariants only — the
    // pill's background stays on the paperLevel scale.
    color: {
      primary: '',
      secondary: '',
    },
  },
  // Apply the flare bg classes ONLY when horizontal — vertical has no
  // `before:`/`after:` content so emitting `before:bg-*` there would be CSS
  // bytes with no visual effect.
  compoundVariants: [
    {
      orientation: 'horizontal',
      detached: true,
      class: 'rounded-[8px] before:hidden after:hidden',
    },
    {
      orientation: 'horizontal',
      paperLevel: 0,
      class: 'before:bg-paper-elevation0 after:bg-paper-elevation0',
    },
    {
      orientation: 'horizontal',
      paperLevel: 1,
      class: 'before:bg-paper-elevation1 after:bg-paper-elevation1',
    },
    {
      orientation: 'horizontal',
      paperLevel: 2,
      class: 'before:bg-paper-elevation2 after:bg-paper-elevation2',
    },
    {
      orientation: 'horizontal',
      paperLevel: 3,
      class: 'before:bg-paper-elevation3 after:bg-paper-elevation3',
    },
    {
      orientation: 'horizontal',
      paperLevel: 4,
      class: 'before:bg-paper-elevation4 after:bg-paper-elevation4',
    },
    {
      orientation: 'horizontal',
      paperLevel: 5,
      class: 'before:bg-paper-elevation5 after:bg-paper-elevation5',
    },
    // Underline — repin the sliding element to the list's bottom edge as a
    // 2px bar. Listed after the paperLevel entries so its bg (below) wins the
    // tailwind-merge conflict against the pill's paper background.
    {
      orientation: 'horizontal',
      indicator: 'underline',
      class: 'top-auto bottom-0 h-[2px] rounded-none before:hidden after:hidden',
    },
    {
      orientation: 'horizontal',
      indicator: 'underline',
      color: 'primary',
      class: 'bg-primary-main',
    },
    {
      orientation: 'horizontal',
      indicator: 'underline',
      color: 'secondary',
      class: 'bg-text-primary',
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    paperLevel: 3,
    indicator: 'pill',
    color: 'primary',
  },
})

/**
 * Tab trigger button. The pill background is provided by the sliding
 * Indicator, so the tab itself only carries the label, its text colour (which
 * changes when selected), the hover/focus/press overlays for non-selected
 * tabs, the keyboard focus ring, and the disabled treatment.
 *
 * State is driven by Base UI attributes on the rendered `<button>`:
 * `aria-selected`, `aria-disabled`, `data-orientation`.
 */
export const tabVariants = tv({
  base: [
    typography['button/medium'],
    // Layout — above the indicator so the label sits on the pill (arbitrary
    // z; the stock `z-10` scale emits no CSS in this preset).
    'relative z-[1] inline-flex select-none items-center justify-center whitespace-nowrap',
    // No radius on the box itself — the active pill is the Indicator; the overlay + focus ring are the ::before / ::after pseudo-elements below.
    'capitalize',
    // Reset button styles
    'cursor-pointer border-none bg-transparent',
    // Label stays text-secondary in every inactive state — Figma keeps the
    // label at 70% on hover/focus/press; only the overlay + ring change.
    'text-text-secondary',
    'focus-visible:outline-none',
    'transition-colors duration-150',
    // Interaction overlay (::before) — hover/focus/pressed render as a
    // rounded-8 rect (Figma node 1487:8669 / strip SVG: 90×34 rect). Position
    // is per-orientation (set in variants): horizontal fills the box L/R and is
    // top-0/bottom-4, so the 4px list gap is the only side spacing (matching the
    // 4px bottom). Sits behind the label (z-[-1]); only non-selected,
    // non-disabled tabs get a colour (selected = Indicator pill).
    'before:pointer-events-none before:absolute before:z-[-1] before:rounded-[8px] before:transition-colors before:content-[""]',
    'aria-[disabled=false]:aria-[selected=false]:hover:before:bg-states-hover',
    'aria-[disabled=false]:aria-[selected=false]:[&:focus-visible:not(:active)]:before:bg-states-focus',
    'aria-[disabled=false]:aria-[selected=false]:active:before:bg-states-selected',
    // Focus ring (::after) — Figma "Focused" (node 13933:9264): 1px white
    // border, rounded-6 (borderradius/sm), inset 2px inside the overlay.
    // Position per-orientation (variants). Keyboard focus only.
    'after:pointer-events-none after:absolute after:rounded-[6px] after:border after:border-white after:opacity-0 after:transition-opacity after:content-[""]',
    'aria-[disabled=false]:[&:focus-visible:not(:active)]:after:opacity-100',
    // Disabled — Figma text/disabled #ffffff2e (white ~18%); text-disabled
    // (white 22%) is the closest DS token. action-disabled is grey-based (wrong).
    'aria-[disabled=true]:cursor-not-allowed aria-[disabled=true]:text-text-disabled',
  ],
  variants: {
    // `medium` classes are byte-identical to the pre-variant `base` metrics, so existing consumers are unchanged.
    // `large`'s 50px is an intentional design height, not a stray off-grid value — it has
    // production consumers beyond the tier-overview format strip: the payment-gateway picker
    // (apps/gto-wizard-web/src/pages/subscription/components/payment-gates-tabs.tsx, on the paid
    // checkout path), apps/rakeback-web, and two trainer settings tabs.
    size: {
      medium: 'h-[38px] min-w-[90px] px-[16px]',
      large: 'h-[50px] min-w-[90px] px-[20px]',
    },
    orientation: {
      // Figma active horizontal pill (node 6579:45113): pt-6 pb-9 px-16,
      // items-center, min-w-90, rounded-t-8. Overlay fills the box L/R and is
      // top-0/bottom-4 (Figma strip: 90×34 rect, y=0); the 4px list gap is the
      // side spacing. Ring inset 2px inside the overlay.
      horizontal:
        'pt-[6px] pb-[9px] before:left-0 before:right-0 before:top-0 before:bottom-[4px] after:left-[2px] after:right-[2px] after:top-[2px] after:bottom-[6px]',
      // Figma vertical pill (node 6579:45159): px-12 py-6, 38px tall,
      // fully-rounded, label left-aligned. Width fills the column. Overlay inset
      // an even 2px (no flare/connector zone in vertical); ring inset +2px.
      vertical: 'w-full justify-start px-[12px] py-[6px] before:inset-[2px] after:inset-[4px]',
    },
    color: {
      primary: 'aria-selected:text-primary-main',
      secondary: 'aria-selected:text-text-primary',
    },
    fullWidth: {
      // Inside a scroll container / full-width row, tabs share the width.
      true: 'min-w-0 flex-1',
    },
    scrollable: {
      // Inside a scroll container, tabs keep their natural size.
      true: 'shrink-0',
    },
    detached: {
      true: '',
    },
  },
  compoundVariants: [
    // Detached horizontal pill has no panel below, so the box is symmetric
    // like the vertical pill: even padding, overlay/ring evenly inset.
    {
      orientation: 'horizontal',
      detached: true,
      class: 'py-[6px] before:inset-[2px] after:inset-[4px]',
    },
  ],
  defaultVariants: {
    size: 'medium',
    orientation: 'horizontal',
    color: 'primary',
  },
})

/**
 * Tabs panel — the content body that sits below (horizontal) or beside
 * (vertical) the tab list. Paints the same `paper-elevationN` surface as the
 * active pill + connector flares so the moustache merges seamlessly into the
 * body.
 *
 * In horizontal orientation the TOP corners are conditional on whether the
 * active tab's left/right edges align with the panel's edges (Figma node
 * 19452:9249, "Container-down-panel"). The panel measures this at runtime —
 * see `panel.tsx` `useLayoutEffect` — because the consumer can't know
 * statically (`fullWidth`, scrollable rows, container resizes, dynamic
 * content widths all affect the alignment). When an edge aligns, that corner
 * stays square so the active pill's moustache flare merges flush; when it
 * doesn't, the corner rounds to clear an inactive tab sitting above. Bottom
 * corners are always rounded.
 *
 * In vertical orientation the active pill is on the LEFT not the TOP, so the
 * moustache mechanism does not apply — all four corners are simply rounded
 * and the `topLeft` / `topRight` variants do not contribute.
 */
export const tabsPanelVariants = tv({
  // `data-[hidden]:hidden` is load-bearing, not cosmetic: with `keepMounted` an
  // inactive panel stays in the DOM carrying Base UI's native `hidden` attribute
  // (see `panel.tsx` — no inline `display:none`). The UA `[hidden]{display:none}`
  // rule is author-overridable, so a consumer that sets a `display` utility on
  // the panel (`flex`, `grid`, …) silently un-hides the inactive panel — both
  // panels then render stacked. This variant re-asserts `display:none` at
  // class+attribute specificity (0,2,0) whenever Base UI marks the panel hidden
  // (`data-hidden`), beating the consumer's bare `.flex` (0,1,0). It only matches
  // while hidden, so an active panel's own display utility is untouched. jsdom
  // never applies this (no Tailwind CSS in unit tests), so it is a browser-only
  // guarantee that NO unit test in this lib can lock — `tabs.test.tsx` asserts the
  // hidden panel's markup, never its computed `display`. The one real lock lives in
  // a consumer's e2e: `apps/gto-wizard-e2e/src/Study/HoldemV2/holdem_ranges.spec.ts`
  // expects the `keepMounted` `strategy-tab-panel` to be HIDDEN while the Ranges
  // soltab is active — that panel sets its own `flex`, so it is exactly the
  // consumer-override case above. Deleting this variant turns that e2e red.
  base: ['focus-visible:outline-none', 'data-[hidden]:hidden'],
  variants: {
    paperLevel: {
      0: 'bg-paper-elevation0',
      1: 'bg-paper-elevation1',
      2: 'bg-paper-elevation2',
      3: 'bg-paper-elevation3',
      4: 'bg-paper-elevation4',
      5: 'bg-paper-elevation5',
    },
    orientation: {
      horizontal: 'rounded-b-[12px]',
      vertical: 'rounded-[12px]',
    },
    topLeft: {
      square: '',
      round: '',
    },
    topRight: {
      square: '',
      round: '',
    },
  },
  compoundVariants: [
    // Horizontal only — square corners contribute nothing (no rounded-tl/tr
    // class), round corners add the 12px radius. compoundVariants gate by
    // `orientation: 'horizontal'` so vertical's full rounding (from the
    // orientation variant above) isn't undone here.
    {
      orientation: 'horizontal',
      topLeft: 'round',
      class: 'rounded-tl-[12px]',
    },
    {
      orientation: 'horizontal',
      topRight: 'round',
      class: 'rounded-tr-[12px]',
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    paperLevel: 3,
    // Default to round on both — the panel switches a corner to `square`
    // ONLY after measurement confirms the active tab's edge aligns. Before
    // measurement (initial paint, no active tab, layout effect not yet run)
    // both corners stay rounded — the safe-looking default.
    topLeft: 'round',
    topRight: 'round',
  },
})

export type TabsColor = 'primary' | 'secondary'
/** Tab height preset — `medium` (38px, default) or `large` (50px). */
export type TabsSize = 'medium' | 'large'
export type TabsOrientation = 'horizontal' | 'vertical'
/** Paper-elevation level used for the active pill + connector flares. */
export type TabsPaperLevel = 0 | 1 | 2 | 3 | 4 | 5
/** Active-indicator shape — the chrome-tab `pill` (default) or a 2px bottom `underline`. */
export type TabsIndicator = 'pill' | 'underline'
/** Per-corner rounding state for the Panel's top corners. */
export type TabsCornerState = 'square' | 'round'
